import { useState } from 'react';
import { 
    MessageCircle, 
    Repeat2, 
    Bookmark, 
    Share2, 
    MoreHorizontal,
    ThumbsUp,
    Heart,
    Lightbulb,
    PartyPopper,
    Search,
    Globe,
    Users,
    Lock,
    Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { 
    Tooltip, 
    TooltipContent, 
    TooltipProvider, 
    TooltipTrigger 
} from './ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { API_URL as API } from '../config';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import CommentSection from './CommentSection';

const REACTION_TYPES = [
    { type: 'like', icon: ThumbsUp, color: 'text-blue-500', label: 'Like' },
    { type: 'love', icon: Heart, color: 'text-red-500', label: 'Love' },
    { type: 'insightful', icon: Lightbulb, color: 'text-amber-500', label: 'Insight' },
    { type: 'celebrate', icon: PartyPopper, color: 'text-inclusion-gold', label: 'Celebrate' },
    { type: 'curious', icon: Search, color: 'text-purple-500', label: 'Curious' },
];

const VisibilityIcon = ({ visibility }) => {
    switch (visibility) {
        case 'connections': return <Users className="w-3 h-3" />;
        case 'private': return <Lock className="w-3 h-3" />;
        default: return <Globe className="w-3 h-3" />;
    }
};

const PostCard = ({ post, onPostUpdated }) => {
    const [showComments, setShowComments] = useState(false);
    const [localReaction, setLocalReaction] = useState(post.user_reaction);
    const [localReactions, setLocalReactions] = useState(post.reactions || {});
    const [isBookmarked, setIsBookmarked] = useState(post.is_bookmarked);
    const [loading, setLoading] = useState(false);

    const handleReact = async (type) => {
        try {
            const response = await axios.post(`${API}/interactions/react/${post.id}`, { reaction_type: type });
            const { action, from, to } = response.data;
            const newReactions = { ...localReactions };
            if (action === 'added') { newReactions[type] = (newReactions[type] || 0) + 1; setLocalReaction(type); }
            else if (action === 'removed') { newReactions[type] = Math.max(0, (newReactions[type] || 1) - 1); setLocalReaction(null); }
            else if (action === 'changed') { newReactions[from] = Math.max(0, (newReactions[from] || 1) - 1); newReactions[to] = (newReactions[to] || 0) + 1; setLocalReaction(to); }
            setLocalReactions(newReactions);
        } catch { toast.error('Failed to react'); }
    };

    const handleBookmark = async () => {
        try {
            await axios.post(`${API}/interactions/bookmark/${post.id}`);
            setIsBookmarked(!isBookmarked);
            toast.success(isBookmarked ? 'Removed from saved' : 'Post saved to collection');
        } catch { toast.error('Failed to bookmark post'); }
    };

    const handleRepost = async (commentary = null) => {
        setLoading(true);
        try {
            await axios.post(`${API}/posts/${post.id}/repost`, { commentary });
            toast.success(commentary ? 'Reposted with your thoughts' : 'Post shared to your feed');
            if (onPostUpdated) onPostUpdated();
        } catch { toast.error('Failed to repost'); }
        finally { setLoading(false); }
    };

    const handleRepostWithCommentary = () => {
        const commentary = window.prompt("Add your thoughts to this repost:");
        if (commentary !== null) handleRepost(commentary);
    };

    const currentReaction = REACTION_TYPES.find(r => r.type === localReaction);
    const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);

    return (
        <div
            className="rounded-xl border mb-4 overflow-hidden transition-colors shadow"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}
        >
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to={`/profile/${post.author_id}`}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold overflow-hidden"
                            style={{ backgroundColor: 'var(--hover-overlay)', color: 'var(--text-muted)' }}>
                            {post.author_avatar ? <img src={post.author_avatar} alt="" className="w-full h-full object-cover" /> : post.author_name?.charAt(0)}
                        </div>
                    </Link>
                    <div>
                        <div className="flex items-center gap-2">
                            <Link to={`/profile/${post.author_id}`}
                                className="font-semibold text-sm transition-colors hover:text-inclusion-gold"
                                style={{ color: 'var(--text-primary)' }}>
                                {post.author_name}
                            </Link>
                            {post.is_repost && <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}>• reposted</span>}
                        </div>
                        <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--text-faint)' }}>
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(post.created_at))} ago
                            <span style={{ color: 'var(--border-subtle)' }}>•</span>
                            <VisibilityIcon visibility={post.visibility} />
                        </div>
                    </div>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" style={{ color: 'var(--text-muted)' }}>
                            <MoreHorizontal className="w-4 h-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}>
                        <DropdownMenuItem className="cursor-pointer gap-2" onClick={handleBookmark}>
                            <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-inclusion-gold text-inclusion-gold' : ''}`} />
                            {isBookmarked ? 'Saved' : 'Save post'}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`)}>
                            <Share2 className="w-4 h-4" /> Copy link
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Content */}
            <div className="px-4 py-2 pb-4">
                <div className="text-sm whitespace-pre-wrap social-content"
                    style={{ color: 'var(--text-secondary)' }}
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />
                {post.is_repost && post.original_post && (
                    <div className="mt-4 border rounded-lg p-4" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--hover-overlay)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                                style={{ backgroundColor: 'var(--active-overlay)', color: 'var(--text-muted)' }}>
                                {post.original_post.author_name?.charAt(0)}
                            </div>
                            <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{post.original_post.author_name}</span>
                            <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}>• {formatDistanceToNow(new Date(post.original_post.created_at))} ago</span>
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}
                            dangerouslySetInnerHTML={{ __html: post.original_post.content }}
                        />
                    </div>
                )}
            </div>

            {/* Reaction summary */}
            <div className="px-4 py-2 flex items-center justify-between text-[11px] border-t"
                style={{ color: 'var(--text-faint)', borderColor: 'var(--border-subtle)' }}>
                <div className="flex items-center gap-1">
                    {totalReactions > 0 && (
                        <>
                            <div className="flex -space-x-1">
                                {Object.keys(localReactions).filter(k => localReactions[k] > 0).slice(0, 3).map(type => {
                                    const RD = REACTION_TYPES.find(r => r.type === type);
                                    const Icon = RD?.icon || ThumbsUp;
                                    return (
                                        <div key={type} className="rounded-full p-0.5 border" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                                            <Icon className={`w-2.5 h-2.5 ${RD?.color}`} />
                                        </div>
                                    );
                                })}
                            </div>
                            <span>{totalReactions} reactions</span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span className="cursor-pointer hover:underline" onClick={() => setShowComments(!showComments)}>
                        {post.comments_count || 0} comments
                    </span>
                    <span>•</span>
                    <span>0 shares</span>
                </div>
            </div>

            {/* Action bar */}
            <div className="p-1 border-t flex items-center gap-1" style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="relative group/react flex-1">
                    {/* Hover reaction bar */}
                    <div className="absolute bottom-full left-0 mb-2 p-1 rounded-full shadow-2xl opacity-0 group-hover/react:opacity-100 transition-opacity duration-200 pointer-events-none group-hover/react:pointer-events-auto flex items-center gap-1 z-10 border"
                        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
                        {REACTION_TYPES.map(r => (
                            <TooltipProvider key={r.type}>
                                <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                        <button className="p-2 rounded-full hover:scale-125 transition-transform duration-200"
                                            style={{ ':hover': { backgroundColor: 'var(--hover-overlay)' } }}
                                            onClick={() => handleReact(r.type)}>
                                            <r.icon className={`w-5 h-5 ${r.color}`} />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }} className="text-[10px] p-1 px-2">
                                        {r.label}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ))}
                    </div>

                    <Button variant="ghost" size="sm"
                        className={`w-full gap-2 text-xs h-9 ${localReaction ? currentReaction?.color : ''}`}
                        style={!localReaction ? { color: 'var(--text-muted)' } : {}}
                        onClick={() => handleReact('like')}>
                        {localReaction ? <currentReaction.icon className="w-4 h-4" /> : <ThumbsUp className="w-4 h-4" />}
                        {localReaction ? currentReaction.label : 'Like'}
                    </Button>
                </div>

                <Button variant="ghost" size="sm" className="flex-1 gap-2 text-xs h-9"
                    style={{ color: 'var(--text-muted)' }}
                    onClick={() => setShowComments(!showComments)}>
                    <MessageCircle className="w-4 h-4" />
                    Comment
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="flex-1 gap-2 text-xs h-9"
                            style={{ color: 'var(--text-muted)' }} disabled={loading}>
                            <Repeat2 className="w-4 h-4" />
                            Repost
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}>
                        <DropdownMenuItem className="gap-2 cursor-pointer text-xs" onClick={() => handleRepost()}>
                            <Repeat2 className="w-3 h-3" /> Repost now
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-2 cursor-pointer text-xs" onClick={handleRepostWithCommentary}>
                            <MessageCircle className="w-3 h-3" /> Repost with thoughts
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {showComments && (
                <div className="border-t p-4" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--hover-overlay)' }}>
                    <CommentSection postId={post.id} onCommentAdded={() => onPostUpdated && onPostUpdated()} />
                </div>
            )}
        </div>
    );
};

export default PostCard;
